import { useForm } from 'react-hook-form';
import { ApiCustomerIdentity } from '../../../openapi';
import { Button, FormContentIsland, Grid, HookForm, PasswordField } from '@razz-team/ui-web';
import { useCallback, useEffect } from 'react';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useSetPasswordMutation, useUpdatePasswordMutation } from '../../../api/identity';
import { useTranslation } from 'react-i18next';
import { useFormError } from '../../../hooks/useFormError';

type FormValues = {
  newPassword: string;
  currentPassword: string;
};

export function UpdatePasswordForm({
  identity,
  onComplete,
}: {
  identity: ApiCustomerIdentity;
  onComplete?: () => void;
}) {
  const { t } = useTranslation();

  const idempotencyKey = useIdempotencyKey();
  const setMutation = useSetPasswordMutation({ idempotencyKey });
  const updateMutation = useUpdatePasswordMutation({ idempotencyKey });
  const activeMutation = identity.hasPassword ? updateMutation : setMutation;

  useEffect(() => {
    if (setMutation.isSuccess || updateMutation.isSuccess) {
      onComplete?.();
    }
  }, [setMutation.isSuccess, updateMutation.isSuccess, onComplete]);

  const form = useForm<FormValues>();

  useFormError(form, 'currentPassword', activeMutation.error);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (identity.hasPassword) {
        updateMutation.mutate(values);
      } else {
        setMutation.mutate({ password: values.newPassword });
      }
    },
    [identity.hasPassword, updateMutation, setMutation],
  );

  const focusCurrent = Boolean(identity?.hasPassword);

  return (
    <Grid
      flexDirection="column"
      gap={4}
      component={HookForm}
      form={form}
      onSubmit={handleSubmit}
      size={12}
    >
      <FormContentIsland>
        <input
          name="username"
          value={identity.email ?? identity.phone ?? ''}
          autoComplete="username"
          readOnly
          style={{ display: 'none' }}
        />
        {identity?.hasPassword ? (
          <PasswordField
            name="currentPassword"
            label={t('Current password')}
            autoComplete="current-password"
            autoFocus={focusCurrent}
            required
          />
        ) : null}
        <PasswordField
          name="newPassword"
          label={t('New password')}
          autoComplete="new-password"
          autoFocus={!focusCurrent}
          required
        />
      </FormContentIsland>
      <Button type="submit" size="large" loading={activeMutation.isPending} fullWidth>
        {t('Submit')}
      </Button>
    </Grid>
  );
}
